import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  message,
  Modal,
  Avatar,
  Tooltip,
  Form,
  Select,
  Spin,
  Empty,
  Alert,
  Divider,
} from 'antd';
import {
  PhoneOutlined,
  ReloadOutlined,
  SyncOutlined,
  EyeOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  RobotOutlined,
  FileDoneOutlined,
  AudioOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { callCenterCallsApi, callCenterAgentsApi } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import PersianDatePicker from '../../components/PersianDatePicker';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import { toJalali } from '../../utils/dateUtils';
import { parseJalaliDateRangeValue, formatJalaliDateForApi } from '../../utils/filterDateUtils';
import { useListReturnState } from '../../hooks/useListReturnState';

const DIRECTION_META = {
  incoming: { label: 'ورودی', color: 'blue', icon: <ArrowDownOutlined /> },
  outgoing: { label: 'خروجی', color: 'green', icon: <ArrowUpOutlined /> },
  missed: { label: 'از دست رفته', color: 'red', icon: <PhoneOutlined /> },
};

const DIRECTION_FILTER_OPTIONS = [
  { value: 'all', label: 'همه' },
  { value: 'incoming', label: 'ورودی' },
  { value: 'outgoing', label: 'خروجی' },
  { value: 'missed', label: 'از دست رفته' },
];

const DISPOSITION_META = {
  ANSWERED: { label: 'پاسخ داده', color: 'green' },
  'NO ANSWER': { label: 'بی‌پاسخ', color: 'orange' },
  NOANSWER: { label: 'بی‌پاسخ', color: 'orange' },
  BUSY: { label: 'مشغول', color: 'gold' },
  FAILED: { label: 'ناموفق', color: 'red' },
  failed: { label: 'خطا', color: 'error' },
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Number(seconds) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const toLatinDigits = (value) => {
  if (value == null) return '';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = String(value);
  for (let i = 0; i < 10; i += 1) {
    result = result.replace(new RegExp(persianDigits[i], 'g'), String(i));
    result = result.replace(new RegExp(arabicDigits[i], 'g'), String(i));
  }
  return result;
};

const formatDatePickerValue = (dateObj) => {
  if (!dateObj) return null;
  if (typeof dateObj === 'string') return toLatinDigits(dateObj).replace(/\//g, '-');
  if (dateObj?.format) return toLatinDigits(dateObj.format('YYYY/MM/DD')).replace(/\//g, '-');
  return null;
};

const buildDefaultCallFilters = () => ({
  direction: 'all',
  startDate: null,
  endDate: null,
  extension: undefined,
  agent_user_id: undefined,
});

const CallCenterCalls = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncForm] = Form.useForm();
  const [loadingExtensions, setLoadingExtensions] = useState(false);
  const [extensionOptions, setExtensionOptions] = useState([]);
  const [calls, setCalls] = useState([]);
  const [filters, setFilters] = useState(buildDefaultCallFilters);
  const [agentOptions, setAgentOptions] = useState([]);
  const [filterExtOptions, setFilterExtOptions] = useState([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [textModalLoading, setTextModalLoading] = useState(false);
  const [textModalCall, setTextModalCall] = useState(null);
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [audioModalLoading, setAudioModalLoading] = useState(false);
  const [audioLoadingId, setAudioLoadingId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioCallLabel, setAudioCallLabel] = useState('');
  const [transcribingIds, setTranscribingIds] = useState([]);

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current_page: pagination.current_page,
        per_page: pagination.per_page,
      },
    }),
    [filters, pagination.current_page, pagination.per_page],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current_page: restored.pagination.current_page || 1,
        per_page: restored.pagination.per_page || prev.per_page,
      }));
    }
  });

  const loadFilterOptions = useCallback(async () => {
    if (agentsLoaded) return;
    try {
      const res = await callCenterAgentsApi.getAll();
      const agents = res?.data ?? [];
      setAgentOptions(
        agents
          .filter((a) => a.user)
          .map((a) => ({
            value: a.user_id,
            label: a.user.name,
          })),
      );
      setFilterExtOptions(
        agents
          .filter((a) => a.extension)
          .map((a) => ({
            value: String(a.extension),
            label: `داخلی ${a.extension}${a.user?.name ? ` — ${a.user.name}` : ''}`,
          })),
      );
      setAgentsLoaded(true);
    } catch {
      // silent
    }
  }, [agentsLoaded]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const buildQueryParams = useCallback((f) => {
    const q = {};
    if (f.direction && f.direction !== 'all') q.direction = f.direction;
    else if (f.direction === 'all') q.direction = 'all';
    if (f.startDate) {
      const from = formatJalaliDateForApi(f.startDate);
      if (from) q.from = from.replace(/-/g, '/');
    }
    if (f.endDate) {
      const to = formatJalaliDateForApi(f.endDate);
      if (to) q.to = to.replace(/-/g, '/');
    }
    if (f.extension) q.extension = f.extension;
    if (f.agent_user_id) q.agent_user_id = f.agent_user_id;
    return q;
  }, []);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchCalls = useCallback(async (page = 1, perPage = 20, activeFilters) => {
    const f = activeFilters ?? filtersRef.current;
    setLoading(true);
    try {
      const q = buildQueryParams(f);
      const res = await callCenterCallsApi.getAll({ page, per_page: perPage, ...q });
      const payload = res?.data ?? {};
      setCalls(payload.data ?? []);
      const total = payload.total ?? 0;
      const per = payload.per_page ?? perPage;
      setPagination({
        current_page: payload.current_page ?? page,
        per_page: per,
        total,
        last_page: Math.max(1, Math.ceil(total / per)),
      });
    } catch (error) {
      console.error('خطا در دریافت تماس‌ها:', error);
      message.error(error?.message || 'خطا در دریافت لیست تماس‌ها');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchCalls(1, pagination.per_page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'dateRange') {
      const { startDate, endDate } = parseJalaliDateRangeValue(value);
      setFilters((prev) => {
        const next = { ...prev, startDate, endDate };
        setTimeout(() => fetchCalls(1, pagination.per_page, next), 0);
        return next;
      });
    } else {
      setFilters((prev) => {
        const next = { ...prev, [key]: value ?? undefined };
        setTimeout(() => fetchCalls(1, pagination.per_page, next), 0);
        return next;
      });
    }
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  }, [fetchCalls, pagination.per_page]);

  const handleResetFilters = useCallback(() => {
    const def = buildDefaultCallFilters();
    setFilters(def);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchCalls(1, pagination.per_page, def);
  }, [fetchCalls, pagination.per_page]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, current_page: page, per_page: pageSize }));
    fetchCalls(page, pageSize);
  };

  const quickFilters = useMemo(() => [
    {
      key: 'direction',
      type: 'select',
      label: 'نوع تماس',
      placeholder: 'نوع تماس',
      value: filters.direction,
      options: DIRECTION_FILTER_OPTIONS,
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'بازه تاریخ',
      placeholder: 'از تاریخ — تا تاریخ',
      value: filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : undefined,
    },
    {
      key: 'extension',
      type: 'select',
      label: 'داخلی',
      placeholder: 'همه داخلی‌ها',
      value: filters.extension,
      options: filterExtOptions,
      showSearch: true,
    },
    {
      key: 'agent_user_id',
      type: 'select',
      label: 'اپراتور',
      placeholder: 'همه اپراتورها',
      value: filters.agent_user_id,
      options: agentOptions,
      showSearch: true,
    },
  ], [filters, filterExtOptions, agentOptions]);

  const openSyncModal = async () => {
    setSyncModalOpen(true);
    syncForm.resetFields();
    setLoadingExtensions(true);
    try {
      const res = await callCenterAgentsApi.getAll();
      const agents = res?.data ?? [];
      const exts = agents
        .filter((a) => a.extension)
        .map((a) => ({
          value: String(a.extension),
          label: `داخلی ${a.extension}${a.user?.name ? ` — ${a.user.name}` : ''}`,
        }));
      setExtensionOptions(exts);
      syncForm.setFieldsValue({
        extensions: exts.map((e) => e.value),
      });
    } catch (error) {
      console.error('خطا در دریافت داخلی‌ها:', error);
      message.error('خطا در دریافت لیست داخلی‌ها');
    } finally {
      setLoadingExtensions(false);
    }
  };

  const submitSync = async () => {
    try {
      const values = await syncForm.validateFields();
      const from = formatDatePickerValue(values.from_date);
      const to = formatDatePickerValue(values.to_date);
      if (!from || !to) {
        message.error('لطفاً تاریخ شروع و پایان را انتخاب کنید');
        return;
      }
      setSyncing(true);
      const res = await callCenterCallsApi.sync({
        sync: true,
        from,
        to,
        extensions: values.extensions ?? [],
      });
      const data = res?.data ?? {};
      if (data.queued) {
        message.success(res?.message || 'همگام‌سازی در صف قرار گرفت');
      } else {
        message.success(
          res?.message ||
          `همگام‌سازی انجام شد — ${data.imported ?? 0} تماس وارد شد`,
        );
      }
      setSyncModalOpen(false);
      await fetchCalls(1, pagination.per_page);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در همگام‌سازی:', error);
      message.error(error?.message || 'خطا در همگام‌سازی تماس‌ها');
    } finally {
      setSyncing(false);
    }
  };

  const openTextModal = async (row) => {
    setTextModalOpen(true);
    setTextModalCall(null);
    setTextModalLoading(true);
    try {
      const res = await callCenterCallsApi.getById(row.id);
      setTextModalCall(res?.data ?? null);
    } catch (error) {
      console.error('خطا در دریافت متن تماس:', error);
      message.error(error?.message || 'خطا در دریافت متن تماس');
      setTextModalOpen(false);
    } finally {
      setTextModalLoading(false);
    }
  };

  const playCallAudio = async (row) => {
    if (!row?.audio_file && !row?.audio_url) {
      message.warning('فایل صوتی برای این تماس در دسترس نیست');
      return;
    }
    setAudioLoadingId(row.id);
    setAudioModalLoading(true);
    setAudioUrl(null);
    setAudioCallLabel(row.phone || `#${row.id}`);
    setAudioModalOpen(true);
    try {
      const res = await callCenterCallsApi.getAudioLink(row.id);
      const link = res?.data?.audio_link;
      if (!link) {
        message.warning('لینک فایل صوتی در دسترس نیست');
        setAudioModalOpen(false);
        return;
      }
      setAudioUrl(link);
    } catch (error) {
      console.error('خطا در دریافت فایل صوتی:', error);
      message.error(error?.message || 'خطا در دریافت فایل صوتی');
      setAudioModalOpen(false);
    } finally {
      setAudioLoadingId(null);
      setAudioModalLoading(false);
    }
  };

  const handleTranscribe = useCallback(async (callId) => {
    setTranscribingIds((prev) => [...prev, callId]);
    try {
      const res = await callCenterCallsApi.transcribe(callId);
      message.success(res?.message || 'درخواست تبدیل صوت به متن در صف پردازش قرار گرفت.');
      setCalls((prev) => prev.map(c => c.id === callId ? { ...c, transcribe_status: 'pending' } : c));
    } catch (error) {
      console.error('Transcription error:', error);
      message.error(error?.message || 'خطا در درخواست رونویسی');
    } finally {
      setTranscribingIds((prev) => prev.filter((id) => id !== callId));
    }
  }, []);

  useEffect(() => {
    const activeCalls = calls.filter(c => c.transcribe_status === 'pending' || c.transcribe_status === 'processing');
    if (activeCalls.length === 0) return;

    const interval = setInterval(async () => {
      for (const call of activeCalls) {
        try {
          const res = await callCenterCallsApi.getById(call.id);
          const updatedCall = res?.data;
          if (updatedCall) {
            setCalls(prev => prev.map(c => c.id === updatedCall.id ? { ...c, ...updatedCall } : c));
          }
        } catch (e) {
          // silent error
        }
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [calls]);

  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        render: (id) => <span className="custom-font" dir="ltr">{id}</span>,
      },
      {
        title: 'نوع تماس',
        dataIndex: 'direction',
        key: 'direction',
        render: (dir) => {
          const meta = DIRECTION_META[dir] || { label: dir || '—', color: 'default' };
          return (
            <Tag color={meta.color} icon={meta.icon} className="custom-font">
              {meta.label}
            </Tag>
          );
        },
      },
      {
        title: 'شماره / مخاطب',
        key: 'contact',
        render: (_, row) => {
          const primary = row.match?.primary;
          const client = row.client;
          const lead = row.lead;
          const deal = row.deal;
          const contactName = primary?.name || client?.name || lead?.client_name || deal?.name;
          const contactType = primary?.type || (client ? 'client' : lead ? 'lead' : deal ? 'deal' : null);
          const phone = row.phone || row.src || '—';

          if (contactName) {
            const typeLabel = contactType === 'client' ? 'مشتری' : contactType === 'lead' ? 'سرنخ' : contactType === 'deal' ? 'معامله' : null;
            const typeColor = contactType === 'client' ? 'green' : contactType === 'lead' ? 'orange' : contactType === 'deal' ? 'blue' : 'default';

            return (
              <div>
                <div className="custom-font font-medium flex items-center gap-1.5 flex-wrap">
                  <span>{contactName}</span>
                  {typeLabel && (
                    <Tag color={typeColor} className="custom-font m-0 text-xs px-1.5 py-0 leading-normal">
                      {typeLabel}
                    </Tag>
                  )}
                </div>
                <div className="custom-font text-xs text-gray-500" dir="ltr">
                  {phone}
                </div>
              </div>
            );
          }
          return <span className="custom-font" dir="ltr">{phone}</span>;
        },
      },
      {
        title: 'اپراتور',
        key: 'agent',
        render: (_, row) => {
          const agent = row.agent;
          if (!agent?.name) return <span className="text-gray-400 custom-font">—</span>;
          return (
            <Space>
              <Avatar src={getImagePath(agent.image)} size={32}>
                {agent.name.charAt(0)}
              </Avatar>
              <span className="custom-font">{agent.name}</span>
            </Space>
          );
        },
      },
      {
        title: 'داخلی',
        dataIndex: 'extension',
        key: 'extension',
        render: (ext) => <span className="custom-font" dir="ltr">{ext || '—'}</span>,
      },
      {
        title: 'وضعیت',
        dataIndex: 'disposition',
        key: 'disposition',
        render: (disp) => {
          const meta = DISPOSITION_META[disp] || { label: disp || '—', color: 'default' };
          return <Tag color={meta.color} className="custom-font">{meta.label}</Tag>;
        },
      },
      {
        title: 'مدت مکالمه',
        dataIndex: 'duration',
        key: 'duration',
        render: (sec) => (
          <span className="custom-font" dir="ltr">{formatDuration(sec)}</span>
        ),
      },
      {
        title: 'زمان',
        dataIndex: 'started_at',
        key: 'started_at',
        render: (dt) => {
          if (!dt) return <span className="text-gray-400 custom-font">—</span>;
          const jalali = toJalali(dt, 'jYYYY/jMM/jDD HH:mm');
          return (
            <span className="custom-font text-sm">{jalali || dt}</span>
          );
        },
      },
      // {
      //   title: 'AI',
      //   dataIndex: 'ai_status',
      //   key: 'ai_status',
      //   render: (status) => {
      //     const meta = AI_STATUS_META[status] || AI_STATUS_META.none;
      //     if (status === 'none' || !status) return <span className="text-gray-400 custom-font">—</span>;
      //     return <Tag color={meta.color} className="custom-font">{meta.label}</Tag>;
      //   },
      // },
      {
        title: 'عملیات',
        key: 'actions',
        width: 140,
        render: (_, row) => {
          const hasAudio = Boolean(row?.audio_file || row?.audio_url);
          const tStatus = row?.transcribe_status || (row?.is_transcribed ? 'completed' : 'none');

          return (
            <Space size={0}>
              {tStatus === 'completed' && (
                <Tooltip title="رونویسی شده">
                  <span className="text-green-500 flex items-center justify-center px-2">
                    <FileDoneOutlined />
                  </span>
                </Tooltip>
              )}
              {tStatus === 'pending' && (
                <Tooltip title="در صف رونویسی...">
                  <span className="text-gray-400 flex items-center justify-center px-2">
                    <ClockCircleOutlined />
                  </span>
                </Tooltip>
              )}
              {tStatus === 'processing' && (
                <Tooltip title="در حال رونویسی... (ممکن است چند دقیقه زمان ببرد)">
                  <span className="text-purple-500 flex items-center justify-center px-2">
                    <LoadingOutlined />
                  </span>
                </Tooltip>
              )}
              {tStatus === 'failed' && (
                <Tooltip title={row.transcribe_error || 'خطا در رونویسی'}>
                  <span className="text-red-500 flex items-center justify-center px-2">
                    <CloseCircleOutlined />
                  </span>
                </Tooltip>
              )}
              {hasAudio && (tStatus === 'none' || tStatus === 'failed') && (
                <Tooltip title="ارسال برای رونویسی">
                  <Button
                    type="link"
                    size="small"
                    icon={<AudioOutlined />}
                    loading={transcribingIds.includes(row.id)}
                    onClick={() => handleTranscribe(row.id)}
                    className="custom-font text-purple-500 hover:text-purple-600"
                  />
                </Tooltip>
              )}
              {hasAudio && (
                <Tooltip title="پخش صدا">
                  <Button
                    type="link"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    loading={audioLoadingId === row.id}
                    onClick={() => playCallAudio(row)}
                    className="custom-font"
                  />
                </Tooltip>
              )}
              <Tooltip title="مشاهده متن تماس">
                <Button
                  type="link"
                  size="small"
                  icon={<FileTextOutlined />}
                  onClick={() => openTextModal(row)}
                  className="custom-font"
                />
              </Tooltip>
              <Tooltip title="مشاهده جزئیات">
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigateToDetail(`/call-center/calls/${row.id}`)}
                  className="custom-font"
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    [navigateToDetail, audioLoadingId, handleTranscribe, transcribingIds],
  );

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                <PhoneOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  تماس‌ها
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  تاریخچه مکالمات ورودی و خروجی
                </p>
              </div>
            </div>
            <Space wrap>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchCalls(pagination.current_page, pagination.per_page)}
                loading={loading}
              >
                بروزرسانی
              </Button>
              <PermissionGate permission="manage_callcenter_setting">
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={syncing}
                  onClick={openSyncModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  همگام‌سازی CDR
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <div className="mb-6">
              <StandardFilterPanel
                quickFilters={quickFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                totalCount={pagination.total}
                showTotalCount
                hideSearch
                compactFilterLayout
              />
            </div>
            {loading && calls.length === 0 ? (
              <TableSkeleton rows={8} columns={8} />
            ) : (
              <div className="w-full max-w-full overflow-x-auto">
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={calls}
                  className="custom-font"
                  style={{ minWidth: 900 }}
                  pagination={{
                    current: pagination.current_page,
                    pageSize: pagination.per_page,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => (
                      <span className="custom-font">{total} تماس</span>
                    ),
                    onChange: handlePageChange,
                  }}
                  locale={{ emptyText: <span className="custom-font">تماسی یافت نشد. برای دریافت تاریخچه، همگام‌سازی CDR را اجرا کنید.</span> }}
                />
              </div>
            )}
          </div>
        </Card>

        <Modal
          title="همگام‌سازی سوابق تماس‌ها (CDR)"
          open={syncModalOpen}
          onCancel={() => setSyncModalOpen(false)}
          onOk={submitSync}
          confirmLoading={syncing}
          destroyOnClose
          okText="شروع همگام‌سازی"
          cancelText="انصراف"
          width={520}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingExtensions}>
            <Form form={syncForm} layout="vertical" className="custom-font" style={{ marginTop: 8 }}>
              <Form.Item
                name="from_date"
                label="از تاریخ"
                rules={[{ required: true, message: 'تاریخ شروع را انتخاب کنید' }]}
              >
                <PersianDatePicker placeholder="انتخاب تاریخ شروع" />
              </Form.Item>

              <Form.Item
                name="to_date"
                label="تا تاریخ"
                rules={[{ required: true, message: 'تاریخ پایان را انتخاب کنید' }]}
              >
                <PersianDatePicker placeholder="انتخاب تاریخ پایان" />
              </Form.Item>

              <Form.Item
                name="extensions"
                label="داخلی‌ها"
                rules={[{ required: true, message: 'حداقل یک داخلی انتخاب کنید' }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="انتخاب داخلی‌ها"
                  popupClassName="custom-font"
                  className="custom-font"
                  options={extensionOptions}
                  notFoundContent={
                    <span className="custom-font text-gray-500">داخلی یافت نشد</span>
                  }
                />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title={
            <span className="custom-font flex items-center gap-2">
              <FileTextOutlined />
              متن تماس
              {textModalCall?.phone ? (
                <span className="text-gray-400 text-sm font-normal" dir="ltr">
                  ({textModalCall.phone})
                </span>
              ) : null}
            </span>
          }
          open={textModalOpen}
          onCancel={() => {
            setTextModalOpen(false);
            setTextModalCall(null);
          }}
          footer={
            <Button onClick={() => setTextModalOpen(false)} className="custom-font">
              بستن
            </Button>
          }
          width={720}
          destroyOnClose
        >
          <Spin spinning={textModalLoading}>
            <div className="custom-font space-y-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {textModalCall?.ai_status === 'failed' ? (
                <Alert
                  type="error"
                  showIcon
                  className="custom-font"
                  message="خطا در پردازش هوش مصنوعی"
                  description={textModalCall.ai_error || 'پردازش با خطا مواجه شد.'}
                />
              ) : null}

              <div>
                <div className="flex items-center gap-2 mb-2 font-medium text-gray-700 dark:text-gray-200">
                  <RobotOutlined />
                  خلاصه مکالمه
                </div>
                {textModalCall?.summary ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr)',
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="custom-font"
                      style={{
                        fontFamily: 'CustomFont, sans-serif',
                        fontSize: 14,
                        lineHeight: 1.75,
                        padding: 12,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.06)',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                        minWidth: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {textModalCall.summary}
                    </div>
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="خلاصه‌ای موجود نیست" />
                )}
              </div>

              <Divider className="my-2" />

              <div style={{ minWidth: 0, maxWidth: '100%' }}>
                <div className="mb-2 font-medium text-gray-700 dark:text-gray-200">
                  رونوشت مکالمه
                </div>
                {textModalCall?.transcript ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr)',
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="custom-font"
                      style={{
                        fontFamily: 'CustomFont, sans-serif',
                        fontSize: 14,
                        lineHeight: 1.75,
                        padding: 16,
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.06)',
                        backgroundColor: '#fff',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                        minWidth: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {textModalCall.transcript}
                    </div>
                  </div>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="رونوشتی موجود نیست" />
                )}
              </div>
            </div>
          </Spin>
        </Modal>

        <Modal
          title={
            <span className="custom-font">
              پخش تماس
              {audioCallLabel ? (
                <span className="text-gray-400 text-sm font-normal mr-2" dir="ltr">
                  ({audioCallLabel})
                </span>
              ) : null}
            </span>
          }
          open={audioModalOpen}
          onCancel={() => {
            setAudioModalOpen(false);
            setAudioUrl(null);
          }}
          footer={
            <Button
              onClick={() => {
                setAudioModalOpen(false);
                setAudioUrl(null);
              }}
              className="custom-font"
            >
              بستن
            </Button>
          }
          width={520}
          destroyOnClose
        >
          {audioModalLoading ? (
            <div className="text-center py-8">
              <Spin tip="در حال دریافت لینک صوتی..." />
            </div>
          ) : audioUrl ? (
            <audio
              key={audioUrl}
              src={audioUrl}
              controls
              autoPlay
              style={{ width: '100%' }}
              className="custom-font"
            >
              مرورگر شما از پخش صوت پشتیبانی نمی‌کند.
            </audio>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="فایل صوتی در دسترس نیست" />
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CallCenterCalls;
